import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Student, Payment, PaymentStatus } from '@/lib/types';

function computeCurrentMonthStatus(
  student: Student,
  payments: Payment[],
): PaymentStatus {
  if (!student.monthly_fee) return 'no_fee';

  const now = new Date();
  const currentMonthPayment = payments.find(p => {
    const paid = new Date(p.paid_at);
    return paid.getFullYear() === now.getFullYear() && paid.getMonth() === now.getMonth();
  });

  if (currentMonthPayment) return 'paid';

  const dueDay = student.due_day ?? 10;
  if (now.getDate() >= dueDay) return 'overdue';
  return 'pending';
}

export function useStudentPayments(profileId: string) {
  const { data, isLoading: loading } = useQuery({
    queryKey: ['student-payments', profileId],
    queryFn: async () => {
      const { data: studentData, error: studentError } = await supabase
        .from('students')
        .select('*')
        .eq('profile_id', profileId)
        .single();

      if (studentError) throw studentError;
      const student = studentData as Student;

      const { data: paymentData, error: paymentError } = await supabase
        .from('payments')
        .select('*')
        .eq('student_id', student.id)
        .order('paid_at', { ascending: false });

      if (paymentError) throw paymentError;
      const payments = (paymentData as Payment[]) ?? [];

      return {
        student,
        payments,
        currentMonthStatus: computeCurrentMonthStatus(student, payments),
      };
    },
    enabled: !!profileId,
  });

  return {
    student: data?.student ?? null,
    payments: data?.payments ?? [],
    currentMonthStatus: data?.currentMonthStatus ?? 'no_fee' as PaymentStatus,
    loading,
  };
}
